import { useCallback, useState } from 'react';
import { Outlet } from 'react-router-dom';
import { Button, Drawer, Icon } from '@gravity-ui/uikit';
import { Bars } from '@gravity-ui/icons';
import { useOrganizations } from '@/shared/auth';
import { useTranslation } from '@/shared/i18n';
import { NoOrganizationsBanner } from '@/shared/ui/states';
import { Logo } from './logo';
import { Sidebar } from './sidebar';
import { useMobile } from './use-mobile';
import './mobile.css';

const COLLAPSED_KEY = 'onemore.sidebarCollapsed';
const MOBILE_MENU_WIDTH = 280;

/**
 * Каркас авторизованной части по макету: боковое меню слева, контент справа
 * на сером фоне. Состояние свёрнутости переживает перезагрузку.
 *
 * На телефоне меню не помещается рядом с контентом: оно уезжает в Drawer,
 * а сверху остаётся шапка с кнопкой. Десктопная раскладка при этом та же.
 */
export function AppLayout() {
  const { organizations } = useOrganizations();
  const mobile = useMobile();
  const [menuOpen, setMenuOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem(COLLAPSED_KEY) === 'true');

  const toggleCollapsed = useCallback(() => {
    setCollapsed((value) => {
      localStorage.setItem(COLLAPSED_KEY, String(!value));

      return !value;
    });
  }, []);

  const content = (
    <main
      style={{
        flexGrow: 1,
        minWidth: 0,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        overflowY: 'auto',
        background: 'var(--g-color-base-generic-ultralight)',
      }}
    >
      {organizations?.length === 0 && (
        <div style={{ padding: '16px 16px 0', flexShrink: 0 }}>
          <NoOrganizationsBanner />
        </div>
      )}

      <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        <Outlet />
      </div>
    </main>
  );

  if (mobile) {
    return (
      <div className="mobile-layout">
        <MobileHeader onOpenMenu={() => setMenuOpen(true)} />

        <Drawer
          open={menuOpen}
          onOpenChange={setMenuOpen}
          placement="left"
          size={MOBILE_MENU_WIDTH}
          className="mobile-menu"
          contentClassName="mobile-menu__panel"
        >
          <Sidebar
            collapsed={false}
            onToggleCollapsed={() => setMenuOpen(false)}
            mobile
            onNavigate={() => setMenuOpen(false)}
          />
        </Drawer>

        {content}
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', height: '100vh', overflow: 'hidden' }}>
      <Sidebar collapsed={collapsed} onToggleCollapsed={toggleCollapsed} />

      {content}
    </div>
  );
}

/** Шапка телефона: меню прячется в выезжающую панель, на экране — кнопка и лого. */
function MobileHeader({ onOpenMenu }: { onOpenMenu: () => void }) {
  const { t } = useTranslation();

  return (
    <header className="mobile-header">
      <Button view="flat" size="l" onClick={onOpenMenu} aria-label={t('sidebar.expandMenu')}>
        <Icon data={Bars} size={18} />
      </Button>
      <Logo label={t('sidebar.logo')} />
    </header>
  );
}
